/**
 * ClientsPage — 接入客户端（docs/ui_page_head_detail 主从布局，按收件域配置）
 * 左栏：收件域选择器（DomainSidebar，含新建/编辑/删除）
 * 右栏：选中收件域下的接入客户端列表（PagedGrid）+ 新建应用 Dialog + API Key/Docker 详情
 * 单客户端绑定一个收件域；仅界面/数据维度，不改动网关鉴权逻辑。
 */
import { useMemo, useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Badge,
  Textarea,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  InfoLabel,
  Switch,
  Divider,
} from '@fluentui/react-components'
import { AddRegular, GlobeRegular, CopyRegular, ApprovalsAppRegular, DismissRegular, PauseRegular, PlayRegular, Delete20Regular } from '@fluentui/react-icons'
import { clientsApi } from '@/api/clients'
import { domainsApi } from '@/api/domains'
import type { ClientApp, Domain } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { PagedGrid, type PagedColumn } from '@/components/PagedGrid'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { DetailGrid2, DetailField } from '@/components/DetailField'
import { formatDate } from '@/lib/format'
import DomainSidebar from '@/components/DomainSidebar'

const PAGE_SIZE = 10

const useStyles = makeStyles({
  root: { padding: '8px 12px', height: '100%', boxSizing: 'border-box' },
  inner: { height: '100%', display: 'flex', flexDirection: 'column' },
  split: { display: 'flex', gap: '16px', alignItems: 'stretch', flex: 1, minHeight: 0, overflow: 'hidden', padding: '2px 0 2px 2px' },
  right: { flex: '1 1 auto', minWidth: 0, height: '100%', boxSizing: 'border-box' },
  rightRoot: { display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%', overflow: 'hidden', boxSizing: 'border-box' },
  rightHeader: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', flexShrink: 0 },
  headerTitle: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1 },
  headerName: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 },
  headerSub: { fontFamily: 'var(--font-monospace, monospace)', fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tabContent: { flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', padding: '0 2px 8px 2px' },
  rightPlaceholder: { height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', color: tokens.colorNeutralForeground3, padding: '40px' },
  tableCard: { padding: '16px' },
  queryRow: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' },
  keywordField: { maxWidth: '320px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 16px' },
  formGridItem: { minWidth: 0 },
  colSpan2: { gridColumn: '1 / -1' },
  dialogSurface: { width: '65vw', minWidth: '600px', maxWidth: '1000px' },
  smallSurface: { width: '480px', maxWidth: '90vw' },
  apiKeyBox: { fontFamily: 'monospace', fontSize: '13px', backgroundColor: tokens.colorNeutralBackground3, padding: '10px 12px', borderRadius: tokens.borderRadiusMedium, wordBreak: 'break-all', margin: '8px 0' },
  warn: { color: tokens.colorStatusWarningForeground1, fontSize: '13px' },
})

function CopyButton({ text }: { text: string }) {
  return (
    <Button appearance="subtle" size="small" icon={<CopyRegular />} onClick={() => { navigator.clipboard.writeText(text); toast.success('已复制') }}>
      复制
    </Button>
  )
}

interface ClientFormState {
  name: string
  description: string
  rate_limit: string
}
const EMPTY_CLIENT_FORM: ClientFormState = { name: '', description: '', rate_limit: '0' }

interface DomainFormState { name: string; description: string }
const EMPTY_DOMAIN_FORM: DomainFormState = { name: '', description: '' }

// 状态徽标文案
function statusText(s: string | undefined): string {
  switch (s) {
    case 'enabled': return '已启用'
    case 'approved': return '已审核'
    case 'pending': return '待审核'
    case 'rejected': return '已拒绝'
    default: return s ?? '-'
  }
}

/** 综合审核状态与启用开关的展示文案 */
function clientStatusText(c: ClientApp): string {
  if (!c.active) return '已停用'
  return statusText(c.status)
}

export default function ClientsPage() {
  const styles = useStyles()
  const queryClient = useQueryClient()

  const { data: domains = [], isLoading: domainsLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: () => domainsApi.list(),
  })
  const { data: clients, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list(),
  })

  // 从 URL 恢复选中域名（便于分享/书签）
  const [searchParams, setSearchParams] = useSearchParams()
  const initDomain = useMemo(() => {
    const v = Number(searchParams.get('domain'))
    return Number.isFinite(v) && v > 0 ? v : null
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedId, setSelectedId] = useState<number | null>(initDomain)

  // 选中态 → URL 同步
  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (selectedId != null) next.set('domain', String(selectedId))
    else next.delete('domain')
    setSearchParams(next, { replace: true })
  }, [selectedId, searchParams, setSearchParams])

  const selectedDomain: Domain | undefined = selectedId != null
    ? domains.find((d) => d.id === selectedId)
    : undefined

  // 选中域名被删除时清空
  useEffect(() => {
    if (selectedId != null && !domains.some((d) => d.id === selectedId)) setSelectedId(null)
  }, [domains, selectedId])

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['domains'] })
    queryClient.invalidateQueries({ queryKey: ['clients'] })
  }

  // ── 新建/编辑收件域 ──
  const [createOpen, setCreateOpen] = useState(false)
  const [domainForm, setDomainForm] = useState<DomainFormState>(EMPTY_DOMAIN_FORM)
  const createDomainMutation = useMutation({
    mutationFn: async () => domainsApi.create({ name: domainForm.name.trim(), description: domainForm.description.trim() || undefined }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      setCreateOpen(false)
      setSelectedId(res.id)
      toast.success('创建成功', '收件域已创建')
    },
    onError: (err) => toast.error('创建失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  const [editTarget, setEditTarget] = useState<Domain | null>(null)
  const [editForm, setEditForm] = useState<DomainFormState>(EMPTY_DOMAIN_FORM)
  const openEdit = (d: Domain) => {
    setEditTarget(d)
    setEditForm({ name: d.name, description: d.description ?? '' })
  }
  const editDomainMutation = useMutation({
    mutationFn: async () => {
      if (!editTarget) return
      await domainsApi.update(editTarget.id, { name: editForm.name.trim(), description: editForm.description.trim() || null })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      setEditTarget(null)
      toast.success('已保存')
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  const [deleteTarget, setDeleteTarget] = useState<Domain | null>(null)
  const deleteDomainMutation = useMutation({
    mutationFn: async () => {
      if (!deleteTarget) return
      await domainsApi.delete(deleteTarget.id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      setDeleteTarget(null)
      toast.success('已删除', '收件域已删除')
    },
    onError: (err) => toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // ── 客户端查询（关键词限定在当前收件域内） ──
  const [keyword, setKeyword] = useState('')
  const domainClients = useMemo(() => {
    const list = (clients ?? []).filter((c) => c.domain === selectedDomain?.name)
    const q = keyword.trim().toLowerCase()
    if (!q) return list
    return list.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      (c.api_key ?? '').toLowerCase().includes(q) ||
      (c.description ?? '').toLowerCase().includes(q),
    )
  }, [clients, selectedDomain, keyword])

  // 分页
  const [page, setPage] = useState(1)
  const totalPages = Math.max(1, Math.ceil(domainClients.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = domainClients.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  // 多选（docs/ui_page §4.2：工具栏「多选」开关切换 single/multiselect）
  const [selectionMode, setSelectionMode] = useState<'single' | 'multiselect'>('single')
  const [selectedRows, setSelectedRows] = useState<ClientApp[]>([])

  useEffect(() => { setPage(1); setSelectionMode('single'); setSelectedRows([]) }, [selectedId])

  // ── 新建接入应用 ──
  const [createClientOpen, setCreateClientOpen] = useState(false)
  const [clientForm, setClientForm] = useState<ClientFormState>(EMPTY_CLIENT_FORM)
  const openCreateClient = () => {
    setClientForm(EMPTY_CLIENT_FORM)
    setCreateClientOpen(true)
  }
  const createClientMutation = useMutation({
    mutationFn: () => {
      const domain = selectedDomain?.name ?? ''
      return clientsApi.create({
        domain,
        name: clientForm.name.trim(),
        description: clientForm.description.trim() || undefined,
        rate_limit: Number(clientForm.rate_limit),
      })
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      setCreatedKey({
        api_key: res.api_key,
        app_key: res.app_key,
        app_secret: res.app_secret,
      })
      setCreateClientOpen(false)
      toast.success('创建成功', '请妥善保管 AppKey 与 AppSecret')
    },
    onError: (err) => toast.error('创建失败', err instanceof ApiError ? err.message : '创建失败'),
  })

  const [createdKey, setCreatedKey] = useState<{ api_key: string; app_key: string; app_secret: string } | null>(null)
  const [detailClient, setDetailClient] = useState<ClientApp | null>(null)

  // ── 删除客户端（批量，列表工具栏多选） ──
  const deleteClientMutation = useMutation({
    mutationFn: async (rows: ClientApp[]) => Promise.all(rows.map((c) => clientsApi.delete(c.id))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      toast.success('删除成功', `已删除 ${selectedRows.length} 项，已移入回收站`)
    },
    onError: (err) => toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // ── 删除单个客户端（详情 Dialog 触发，软删除 → 回收站） ──
  const [deleteSingleTarget, setDeleteSingleTarget] = useState<ClientApp | null>(null)
  const deleteSingleMutation = useMutation({
    mutationFn: async () => {
      if (!deleteSingleTarget) return
      await clientsApi.delete(deleteSingleTarget.id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['recycle-bin'] })
      toast.success('已删除', `${deleteSingleTarget?.name} 已移入回收站，可在回收站中恢复`)
      setDeleteSingleTarget(null)
      setDetailClient(null)
    },
    onError: (err) => toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // ── 审核客户端（通过 / 拒绝） ──
  const reviewMutation = useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'approve' | 'reject' }) => clientsApi.review(id, action),
    onSuccess: (_data, { action }) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      setDetailClient(null)
      toast.success(action === 'approve' ? '审核通过' : '已拒绝', action === 'approve' ? '该客户端已可使用' : '该客户端已被拒绝')
    },
    onError: (err) => toast.error('审核失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // ── 启用 / 停用（取消接入） ──
  const setActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => clientsApi.setActive(id, active),
    onSuccess: (_data, { active }) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      setDetailClient(null)
      toast.success(active ? '已启用' : '已停用', active ? '该客户端已恢复接入' : '该客户端已取消接入，无法再调用 API')
    },
    onError: (err) => toast.error('操作失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  const countLabel = (d: Domain) => {
    const n = (clients ?? []).filter((c) => c.domain === d.name).length
    return `${n} 个接入应用`
  }

  const columns: PagedColumn<ClientApp>[] = [
    {
      key: 'name',
      header: '应用名称',
      headerTooltip: '接入网关的第三方应用名称',
      width: '200px',
      render: (c) => <Text weight="semibold">{c.name}</Text>,
    },
    {
      key: 'api_key',
      header: 'API Key',
      headerTooltip: '接入鉴权密钥，仅显示前 16 位',
      width: '220px',
      render: (c) => (c.api_key ? `${c.api_key.slice(0, 16)}...` : '-'),
    },
    {
      key: 'rate_limit',
      header: '限速',
      headerTooltip: '每分钟允许的请求数，0 表示不限速',
      width: '120px',
      render: (c) => (c.rate_limit === 0 ? '不限速' : `${c.rate_limit}/分`),
    },
    {
      key: 'status',
      header: '状态',
      width: '100px',
      render: (c) => clientStatusText(c),
    },
    {
      key: 'created_at',
      header: '创建时间',
      width: '160px',
      render: (c) => formatDate(c.created_at),
    },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.split}>
          {/* ── 左栏：收件域选择器 ── */}
          <DomainSidebar
            domains={domains}
            loading={domainsLoading}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onNew={() => { setDomainForm(EMPTY_DOMAIN_FORM); setCreateOpen(true) }}
            onEdit={openEdit}
            onDelete={setDeleteTarget}
            onRefresh={refresh}
            countLabel={countLabel}
            emptyHint="暂无收件域，点击左上角「+」新建"
          />

          {/* ── 右栏：选中收件域的接入客户端 ── */}
          <div className={styles.right}>
            {selectedDomain ? (
              <div className={styles.rightRoot}>
                <div className={styles.rightHeader}>
                  <div className={styles.headerTitle}>
                    <span className={styles.headerName}>
                      <GlobeRegular />
                      {selectedDomain.name}
                      <Badge appearance="filled" color={selectedDomain.active ? 'success' : 'subtle'} size="small">
                        {selectedDomain.active ? '活跃' : '停用'}
                      </Badge>
                    </span>
                    <span className={styles.headerSub}>
                      {selectedDomain.description || '收件域'} · 该域名的接入客户端集中配置在本页
                    </span>
                  </div>
                  <Button appearance="primary" icon={<AddRegular />} onClick={openCreateClient}>新建应用</Button>
                </div>

                <div className={styles.tabContent}>
                  <div className={styles.queryRow}>
                    <Input
                      className={styles.keywordField}
                      placeholder="搜索应用名称 / API Key…"
                      value={keyword}
                      onChange={(_, d) => setKeyword(d.value)}
                    />
                  </div>
                  <div className={styles.tableCard}>
                    <PagedGrid<ClientApp>
                      rows={pageItems}
                      columns={columns}
                      loading={clientsLoading}
                      rowKey={(c) => String(c.id)}
                      total={domainClients.length}
                      page={safePage}
                      pageSize={PAGE_SIZE}
                      onPageChange={setPage}
                      emptyText="该收件域暂无接入应用"
                      toolbarLeft={
                        <Switch
                          checked={selectionMode === 'multiselect'}
                          onChange={(_, d) => setSelectionMode(d.checked ? 'multiselect' : 'single')}
                          label="多选"
                        />
                      }
                      selectionMode={selectionMode}
                      onSelectionChange={setSelectedRows}
                      onDelete={async (rows) => { await deleteClientMutation.mutateAsync(rows) }}
                      onRowClick={(c) => setDetailClient(c)}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.rightPlaceholder}>
                <GlobeRegular style={{ fontSize: '40px' }} />
                <Text>从左侧选择一个收件域，集中管理该域名的接入客户端</Text>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 新建收件域 */}
      <Dialog open={createOpen} onOpenChange={(_, d) => { if (!d.open) setCreateOpen(false) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="新建收件域" onClose={() => setCreateOpen(false)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field className={styles.formGridItem} label={<InfoLabel info="收件域 / 投递域的完整域名，例如 example.com。">域名</InfoLabel>} required>
                  <Input value={domainForm.name} onChange={(_, d) => setDomainForm((f) => ({ ...f, name: d.value }))} placeholder="例如 example.com" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="该域的用途说明，仅作管理备注。">描述</InfoLabel>}>
                  <Input value={domainForm.description} onChange={(_, d) => setDomainForm((f) => ({ ...f, description: d.value }))} placeholder="可选" />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => createDomainMutation.mutate()} disabled={createDomainMutation.isPending || !domainForm.name.trim()}>
                {createDomainMutation.isPending ? '创建中…' : '创建'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 编辑收件域 */}
      <Dialog open={!!editTarget} onOpenChange={(_, d) => { if (!d.open) setEditTarget(null) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="编辑收件域" onClose={() => setEditTarget(null)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field className={styles.formGridItem} label={<InfoLabel info="收件域 / 投递域的完整域名，例如 example.com。">域名</InfoLabel>} required>
                  <Input value={editForm.name} onChange={(_, d) => setEditForm((f) => ({ ...f, name: d.value }))} placeholder="例如 example.com" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="该域的用途说明，仅作管理备注。">描述</InfoLabel>}>
                  <Input value={editForm.description} onChange={(_, d) => setEditForm((f) => ({ ...f, description: d.value }))} placeholder="可选" />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => editDomainMutation.mutate()} disabled={editDomainMutation.isPending || !editForm.name.trim()}>
                {editDomainMutation.isPending ? '保存中…' : '保存'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 删除收件域确认 */}
      <Dialog open={!!deleteTarget} onOpenChange={(_, d) => { if (!d.open) setDeleteTarget(null) }} modalType="alert">
        <DialogSurface className={styles.smallSurface}>
          <DialogBody>
            <DialogCloseTitle title="删除收件域" onClose={() => setDeleteTarget(null)} />
            <DialogContent>
              <Text>确定删除收件域 <Text weight="semibold">{deleteTarget?.name}</Text> 吗？该操作不可撤销。</Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteTarget(null)}>取消</Button>
              <Button appearance="primary" onClick={() => deleteDomainMutation.mutate()} disabled={deleteDomainMutation.isPending}>
                {deleteDomainMutation.isPending ? '删除中…' : '删除'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 新建接入应用（绑定当前收件域） */}
      <Dialog open={createClientOpen} onOpenChange={(_, d) => { if (!d.open) setCreateClientOpen(false) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="新建接入应用" onClose={() => setCreateClientOpen(false)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field className={styles.formGridItem} label={<InfoLabel info="接入应用所属的收件域，创建后不可迁移。">所属收件域</InfoLabel>} required>
                  <Input value={selectedDomain?.name ?? ''} readOnly />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="接入网关的第三方应用名称，用于识别调用方。">应用名称</InfoLabel>} required>
                  <Input value={clientForm.name} onChange={(_, d) => setClientForm((f) => ({ ...f, name: d.value }))} placeholder="例如：订单系统、营销平台" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="每分钟允许的请求数，0 表示不限速。">限速（封/分钟）</InfoLabel>} hint="0 为不限">
                  <Input type="number" value={clientForm.rate_limit} onChange={(_, d) => setClientForm((f) => ({ ...f, rate_limit: d.value }))} />
                </Field>
                <Field className={`${styles.formGridItem} ${styles.colSpan2}`} label={<InfoLabel info="应用的用途说明，仅作管理备注。">描述</InfoLabel>}>
                  <Textarea value={clientForm.description} onChange={(_, d) => setClientForm((f) => ({ ...f, description: d.value }))} placeholder="应用用途说明" />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                disabled={createClientMutation.isPending || !clientForm.name.trim()}
                onClick={() => createClientMutation.mutate()}
              >
                {createClientMutation.isPending ? '创建中…' : '创建'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 创建成功：凭据展示 Dialog（AppKey / AppSecret） */}
      <Dialog open={createdKey !== null} onOpenChange={(_, d) => { if (!d.open) setCreatedKey(null) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="接入凭据已生成" onClose={() => setCreatedKey(null)} />
            <DialogContent>
              <Text className={styles.warn}>请立即复制保存，AppSecret 仅在创建时完整展示，之后只能再次查看（明文存储）。</Text>
              <Divider style={{ margin: '12px 0' }} />
              <Field label="AppKey（请求头 X-App-Key）" hint="标识客户端身份的公开凭据">
                <Input value={createdKey?.app_key ?? ''} readOnly contentAfter={<CopyButton text={createdKey?.app_key ?? ''} />} />
              </Field>
              <Field label="AppSecret（做签名，请求头外传）" hint="配合 AppKey 对请求做 HMAC-SHA256 签名，切勿泄露">
                <Input value={createdKey?.app_secret ?? ''} readOnly contentAfter={<CopyButton text={createdKey?.app_secret ?? ''} />} />
              </Field>
              <Field label="API Key（旧式 X-API-Key 直连）" hint="向后兼容的旧直连凭据，新接入建议使用 AppKey + 签名">
                <Input value={createdKey?.api_key ?? ''} readOnly contentAfter={<CopyButton text={createdKey?.api_key ?? ''} />} />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setCreatedKey(null)}>我已保存</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 接入应用详情 Dialog */}
      <Dialog open={detailClient !== null} onOpenChange={(_, d) => !d.open && setDetailClient(null)} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="接入应用详情" onClose={() => setDetailClient(null)} />
            <DialogContent>
              {detailClient && (
                <DetailGrid2 key={detailClient.id} cols={2}>
                  <DetailField label="ID" value={detailClient.id} mono />
                  <DetailField label="所属收件域" value={detailClient.domain ?? '-'} mono help="接入应用绑定的收件域。" />
                  <DetailField label="应用名称" value={detailClient.name} help="接入网关的第三方应用名称，用于识别调用方。" />
                  <DetailField label="AppKey" value={detailClient.app_key ?? '-'} mono help="调用发信 API 时放入请求头 X-App-Key，标识客户端身份。" />
                  <DetailField label="AppSecret" value={detailClient.app_secret ?? '-'} mono help="配合 AppKey 对请求做 HMAC-SHA256 签名（明文存储，可查看）。" />
                  <DetailField label="API Key" value={detailClient.api_key ? `${detailClient.api_key.slice(0, 16)}…` : '-'} mono help="旧式直连凭据（X-API-Key），向后兼容，新接入建议使用 AppKey + 签名。" />
                  <DetailField label="限速" value={detailClient.rate_limit === 0 ? '不限速' : `${detailClient.rate_limit}/分`} help="每分钟允许的请求数，0 表示不限速。" />
                  <DetailField label="状态" value={clientStatusText(detailClient)} />
                  <DetailField label="描述" value={detailClient.description ?? '-'} colSpan />
                  <DetailField label="创建时间" value={detailClient.created_at} />
                </DetailGrid2>
              )}
            </DialogContent>
            <DialogActions>
              {detailClient?.status === 'pending' && (
                <>
                  <Button appearance="primary" icon={<ApprovalsAppRegular />} disabled={reviewMutation.isPending} onClick={() => reviewMutation.mutate({ id: detailClient.id, action: 'approve' })}>
                    通过
                  </Button>
                  <Button icon={<DismissRegular />} disabled={reviewMutation.isPending} onClick={() => reviewMutation.mutate({ id: detailClient.id, action: 'reject' })}>
                    拒绝
                  </Button>
                </>
              )}
              {detailClient?.status === 'approved' && (
                detailClient.active
                  ? <Button appearance="secondary" icon={<PauseRegular />} disabled={setActiveMutation.isPending} onClick={() => setActiveMutation.mutate({ id: detailClient.id, active: false })}>停用</Button>
                  : <Button appearance="primary" icon={<PlayRegular />} disabled={setActiveMutation.isPending} onClick={() => setActiveMutation.mutate({ id: detailClient.id, active: true })}>启用</Button>
              )}
              {detailClient && (
                <Button
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  onClick={() => setDeleteSingleTarget(detailClient)}
                  style={{ marginLeft: 'auto', color: tokens.colorStatusDangerForeground1 }}
                >
                  删除
                </Button>
              )}
              <Button appearance="secondary" onClick={() => setDetailClient(null)}>关闭</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 删除接入应用确认（软删除 → 回收站） */}
      <Dialog
        open={!!deleteSingleTarget}
        onOpenChange={(_, d) => { if (!d.open) setDeleteSingleTarget(null) }}
        modalType="alert"
      >
        <DialogSurface className={styles.smallSurface}>
          <DialogBody>
            <DialogCloseTitle title="删除接入应用" onClose={() => setDeleteSingleTarget(null)} />
            <DialogContent>
              <Text>
                确定删除接入应用 <Text weight="semibold">{deleteSingleTarget?.name}</Text> 吗？
                <br />
                删除后数据将移入回收站，可在「回收站」中恢复。
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteSingleTarget(null)}>取消</Button>
              <Button
                appearance="primary"
                onClick={() => deleteSingleMutation.mutate()}
                disabled={deleteSingleMutation.isPending}
              >
                {deleteSingleMutation.isPending ? '删除中…' : '确认删除'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}